import { useCallback, useEffect, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { Menu, History, UserRound } from "lucide-react"
import styled from "styled-components"
import { SideNav } from "./SideNav"
import { PageContextBar } from "./PageContextBar"
import { RequestActivity } from './feedback/LoadingState'
import { useAuth } from '../context/AuthContext'
import './console.css'

const Container = styled.div`
  /* Keep document scrolling from moving the navigation or leaving a bottom gap. */
  position: fixed;
  inset: 0;
  display: flex;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: var(--canvas);
`

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
`

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overscroll-behavior-y: contain;
`

const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 8px;
  background: var(--accent-soft);
  color: var(--accent);
  cursor: pointer;
  &:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
`

const MobileHistoryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  width: 36px;
  border-radius: 10px;
  background: transparent;
  color: var(--muted);
  font-size: .8rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  &:hover { background: var(--accent-soft); color:var(--accent); }
  &:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
`

const Backdrop = styled.button`
  display: none;
  @media only screen and (max-width: 599px) {
    display: block;
    position: absolute;
    inset: 0;
    z-index: 1000;
    width: 100%;
    border: 0;
    background: rgba(11, 15, 26, .6);
  }
`


export function Layout({ children }) {
  const auth = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButtonRef = useRef(null)
  const drawerRef = useRef(null)
  const closeButtonRef = useRef(null)
  const location = useLocation()
  const closeMenu = useCallback(() => {
    setMenuOpen(false)
    menuButtonRef.current?.focus()
  }, [])
  useEffect(() => setMenuOpen(false), [location.pathname])
  useEffect(() => {
    if (!menuOpen) return undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    closeButtonRef.current?.focus()
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeMenu()
      if (event.key !== "Tab") return
      const focusable = Array.from(drawerRef.current?.querySelectorAll('a[href], button:not([disabled]), summary') || []).filter(node => !node.closest('details:not([open])') || node.tagName === 'SUMMARY')
      if (!focusable?.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    const onResize = () => {
      if (window.innerWidth >= 600) {
        setMenuOpen(false)
        drawerRef.current?.querySelector('[aria-current="page"]')?.focus()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    window.addEventListener("resize", onResize)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("resize", onResize)
    }
  }, [menuOpen, closeMenu])

  if (['/login','/signup','/forgot-password','/protocol-handler'].includes(location.pathname.toLowerCase()) || (location.pathname==='/' && auth?.loading===false && !auth.isLoggedIn)) return <div className="console-shell">{children}</div>

  return (
    <Container className="console-shell">
      <a className="console-skip" href="#Contents">본문으로 이동</a>
      <SideNav mobileOpen={menuOpen} onClose={closeMenu} drawerRef={drawerRef} closeButtonRef={closeButtonRef} />
      {menuOpen && <Backdrop type="button" aria-label="메뉴 닫기" onClick={closeMenu} />}
      <MainContent className="console-main">
        <PageContextBar menuControl={<MenuButton ref={menuButtonRef} type="button" aria-label="메뉴 열기" aria-controls="skynet-navigation" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}><Menu size={21} /></MenuButton>}
          mobileActions={<><MobileHistoryLink to="/containers" aria-label="컨테이너 운영 이력 보기" title="운영 이력"><History size={19}/></MobileHistoryLink><MobileHistoryLink to="/account" aria-label="계정 관리" title="계정 관리"><UserRound size={19}/></MobileHistoryLink></>} />
        <RequestActivity />
        <ContentArea className="console-content" key={location.pathname}>{children}</ContentArea>
      </MainContent>
    </Container>
  )
}
